import { Instance, types } from 'mobx-state-tree'
import { Role, RoleModel, RPIC_ROLE } from './RoleModel'
import { IsEditingModel } from '../IsEditingModel'
import { parseBackendUtcDateString } from 'utils/datetimeUtils'
import { emailValidationMessage, MAX_INPUT_LENGTH } from 'utils/validationUtils'

export enum PermissionTypes {
  Edit = 'edit',
  View = 'view',
}

export enum UserPermissionsKeyTypes {
  Checklists = 'checklists',
  FlightNetwork = 'flight_network',
  FlightSimulation = 'flight_simulation',
  LiveMissions = 'live_missions',
  Maintenance = 'maintenance',
  Manuals = 'manuals',
  MissionHistory = 'mission_history',
  PartNumbers = 'part_numbers',
  SysAdmin = 'sys_admin',
  Users = 'users',
  Vehicles = 'vehicles',
  DiscrepancyReportSubmit = 'discrepancy_report_submit',
  DiscrepancyReports = 'discrepancy_reports',
  ChecklistReports = 'checklist_reports',
}

const UserBaseModel = IsEditingModel.named('UserBaseModel')
  .props({
    name: types.string,
    email: types.string,
    creation_timestamp: types.optional(types.maybeNull(types.string), () => new Date().toISOString()),
    roles: types.array(types.reference(RoleModel)),
    max_vehicles_per_rpic: types.maybeNull(types.integer),
  })
  .views((self) => ({
    get creationTimestampDate(): Date | null {
      return self.creation_timestamp === null ? null : parseBackendUtcDateString(self.creation_timestamp)
    },
    get hasRpicRole(): boolean {
      return self.roles.some(role => role.value === RPIC_ROLE)
    },
  }))
  .actions((self) => ({
    setName(value: typeof self.name) {
      self.name = value
    },
    setEmail(value: typeof self.email) {
      self.email = value
    },
    setMaxVehiclesPerRpic(value: number | null) {
      self.max_vehicles_per_rpic = value
    },
    toggleRole(role: Role) {
      if (self.roles.includes(role)) {
        self.roles.remove(role)
      } else {
        self.roles.push(role)
      }
    },
  }))
  .views((self) => ({
    emailValidationMessages(saveInProgress: boolean): string {
      return emailValidationMessage(self.email, saveInProgress)
    },
    nameValidationMessages(saveInProgress: boolean) : string {
      if (saveInProgress && self.name.length === 0){
        return `*Required field`
      }
      if (self.name.length > MAX_INPUT_LENGTH){
        return `*This field is limited to ${MAX_INPUT_LENGTH} characters`
      }
      return ``
    },
    get getRolesValidationMessage(): string {
      if (self.roles.length == 0){
        return `*At least one role must be selected`
      }
      return ``
    },
    get maxVehiclesPerRpicValidationMessage(): string {
      if (!self.hasRpicRole) return ``
      if (self.max_vehicles_per_rpic === null) return `* Required for RPIC users`
      if (self.max_vehicles_per_rpic <= 0) return `* Must be a positive number`
      return ``
    }
  }))
  .views((self) => ({
    get isValid(): boolean{
      if (self.emailValidationMessages(true)!=''){
        console.warn('email is not valid')
        return false
      }
      if (self.nameValidationMessages(true)!=``){
        console.warn('user name is not valid')
        return false
      }
      if (self.getRolesValidationMessage!=''){
        console.warn('no role selected')
        return false
      }
      if (self.maxVehiclesPerRpicValidationMessage!=''){
        console.warn('max_vehicles_per_rpic is not valid')
        return false
      }

      return true
    },
  }))

export const ExistingUserModel = UserBaseModel.named('ExistingUserModel').props({
  id: types.identifierNumber,
})

export const NewUserModel = UserBaseModel.named('NewUserModel') // No additional props, just what UserBaseModel has

export const UserModel = types.union(ExistingUserModel, NewUserModel)

// When a user object is edited, we edit not the instance in the user store, but a temporary clone (in order not to change the object displayed in the table).
// However, in objects with an identifier, and are referenced by this identifier,  we can't have two instances of the same type with the same Id, so we can't create a clone.
// To circumvent this, we define a new type for editing. Now the edit clones are not of the same type, so we don't have this problem.
export const ExistingUserEditCloneModel = ExistingUserModel.named('ExistingUserEditCloneModel')
export const NewUserEditCloneModel = NewUserModel.named('NewUserEditCloneModel')

export const UserEditCloneModel = types.union(ExistingUserEditCloneModel, NewUserEditCloneModel)

export type User = Instance<typeof UserModel>
export type ExistingUser = Instance<typeof ExistingUserModel>
export type UserEditClone = Instance<typeof UserEditCloneModel>

export interface UserPermissionsObject extends Record<UserPermissionsKeyTypes, PermissionTypes[]> {}

export const CurrentUserModel = ExistingUserModel.named('CurrentUserModel')

export const UserAndPermissionsModel = types.model('UserAndPermissionsModel').props({
  user_data: CurrentUserModel,
  permissions: types.map(types.array(types.enumeration('PermissionTypes', Object.values(PermissionTypes)))),
})
